import { ReactNode, useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { useCampaign } from '../contexts/CampaignContext'
import Sidebar from './Sidebar'
import { CallbacksNotificationCenter } from './callbacks/CallbacksNotificationCenter'
import MissedCallsBadge from './MissedCallsBadge'
import { PullToRefresh } from './PullToRefresh'
import { CampaignsService } from '../services'
import ContactsService from '../services/contacts'
import { perfMonitor } from '../utils/performanceMonitor'
// LOT-SUNSET-2 : la téléphonie v1 du châssis est DÉMONTÉE. Le châssis
// n'initialise plus aucun Device Twilio (`globalTwilioListener` supprimé), ne
// se connecte plus au WebSocket /calls (`calls-socket` supprimé — le serveur
// n'expose plus cette gateway depuis SUNSET-1) et ne présente plus
// l'`IncomingCallModal` v1 : les entrants sont présentés sur TOUTE page par
// l'`IncomingCallOverlay` du `TelephonyProvider` v2 (monté au-dessus du châssis
// dans App.tsx), qui possède le Device et la socket /telephony.
// `TelephonyStatusBadge` (v2) dit l'état du poste réellement actif — c'est lui
// qui porte aussi le chemin de réparation quand l'initialisation échoue.
import { TelephonyStatusBadge } from '../telephony/TelephonyStatusBadge'
// Issue #220 (D1) : la barre d'appel globale est fixe en bas, hors du flux ;
// le châssis lit SA règle d'affichage pour dégager le bas du `<main>`.
import { useActiveCallBarShown } from '../telephony/ActiveCallBar'
import { formatTime } from '../utils/format'
import { avatarInitial } from '../utils/avatarInitial'
import {
  LogOut,
  ChevronDown,
  User,
  Clock,
  LayoutDashboard,
  Phone,
  Users,
  Target,
  UserCog,
  Building2,
  Settings,
  Briefcase,
  Upload,
  BarChart3,
  PhoneForwarded,
  FileText,
  HeartHandshake,
  History
} from 'lucide-react'
// L-NAV-8 — icône du bandeau « Aujourd'hui » (même icône que le menu).
import { Home } from 'lucide-react'
import { NotificationRegion } from './notifications/NotificationRegion'
import { TrialBanner } from './gating/TrialBanner'
import { useGatingStatus } from '../hooks/useGatingStatus'
import { usePermissions } from '../hooks/usePermissions'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useTwilioBalanceBanner } from '../hooks/useTwilioBalanceBanner'
import { TwilioBalanceBanner } from './twilio-balance/TwilioBalanceBanner'

interface AppShellProps {
  children: ReactNode
}

/**
 * UNE SEULE FORME DE PILULE DANS LE HEADER (LOT-H11, audit §4).
 *
 * L'audit comptait « cinq formes, cinq hauteurs perçues » sur la rangée : fil
 * d'ariane (pilule bordée `rounded-2xl`), statut (pilule pleine), horloge
 * (pilule bordée), cloche (cercle), carte utilisateur (`rounded-lg`). Un seul
 * gabarit désormais — 32 px de haut, rayon `pill`, bordure et surface neutres —
 * partagé par le fil d'ariane, l'horloge et le sélecteur de campagne, et repris
 * par la pastille de statut (`TelephonyStatusBadge` depuis LOT-SUNSET-2).
 *
 * `h-8` + `whitespace-nowrap` ne sont pas cosmétiques : c'est ce qui garantit
 * qu'AUCUN contenu ne peut faire GRANDIR la rangée (audit §8.2).
 *
 * ⚠️ Le gabarit ne porte PAS `shrink-0`, et ce n'est pas un oubli. Deux
 * utilitaires Tailwind du même groupe ne s'arbitrent pas par leur ordre dans
 * la chaîne de classes mais par leur ordre dans la FEUILLE compilée, où
 * `shrink-0` passe après `shrink` : mettre `shrink-0` ici rendait le
 * `shrink` du sélecteur de campagne inopérant — mesuré à 768 px, la pilule
 * dépassait alors de 19 px le bord du bandeau. Chaque appelant déclare donc
 * lui-même s'il cède ou non.
 */
const HEADER_CHIP =
  'flex h-8 items-center gap-2 whitespace-nowrap rounded-pill border border-border-secondary bg-background-secondary px-3 text-t5 text-foreground-secondary'

// Get page title i18n key + icon based on route. Returns a `titleKey` (resolved
// with `t()` in the component) so this function stays pure and outside React.
//
// #281 — un chemin INCONNU rend `titleKey: null` : l'onglet affiche « iCall CRM »
// seul et le bandeau n'affiche pas de puce (avant : repli silencieux sur
// « Analyse », faux sur toute page absente de la table).
//
// L-NAV-1 — UNE ENTRÉE PAR ROUTE de `App.tsx` (garde :
// `AppShell.routeTitles.spec.ts`, qui parcourt le source) et le libellé est le
// TITRE DE LA PAGE, mot pour mot (`Historique SMS`, pas « Journaux SMS »).
// Les six chemins fusionnés par LOT-H12 (`/admin/team`, `/sms/templates`…)
// ne sont que des redirections : leurs entrées mortes sont retirées.
export function getPageInfo(
  rawPathname: string,
  options: { isAgent?: boolean } = {},
): { titleKey: string | null; icon: any } {
  // `/historique/` et `/historique` sont la même page.
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, '') : rawPathname
  // Recette banc (L-NAV-2) : l'agent lit SES chiffres sous « Mon activité »
  // dans le menu — le bandeau et l'onglet disent la même chose que le menu.
  if (options.isAgent && (pathname === '/analytics' || pathname === '/')) {
    return { titleKey: 'pages.myActivity', icon: BarChart3 }
  }
  const routes: Record<string, { titleKey: string; icon: any }> = {
    '/': { titleKey: 'pages.analyse', icon: LayoutDashboard },
    // LOT-H12 : 4 pages de statistiques → 2 ; les anciens chemins redirigent.
    '/analytics': { titleKey: 'pages.analyse', icon: BarChart3 },
    // L-NAV-8 — `/supervision` est devenue « Aujourd'hui » (`/accueil`) et n'est
    // plus qu'une redirection : le bandeau disait « Supervision » (constat de
    // recette), il dit le titre de la page.
    '/accueil': { titleKey: 'pages.today', icon: Home },
    // fin L-NAV-8
    // LOT-SUNSET-2 : /softphone, /simple-dialer, /admin/telephony-lab et
    // /incoming-call/* sont des redirections (ou ont disparu) — leurs entrées
    // de titre sont retirées. /v2/softphone est la surface d'appel.
    '/v2/softphone': { titleKey: 'pages.phone', icon: Phone },
    '/contacts': { titleKey: 'pages.contacts', icon: Users },
    '/import': { titleKey: 'pages.import', icon: Upload },
    '/import/intelligent': { titleKey: 'pages.importSmart', icon: Upload },
    // L-NAV-6 — `/donations/history` est une redirection vers Contacts
    // (`?tab=donors`) : son entrée de titre est retirée.
    // #245 — page de retour du paiement AlloDons (route ajoutée sur master).
    '/don/merci': { titleKey: 'pages.donationThanks', icon: HeartHandshake },
    // L-NAV-7 — « Équipe » (le bandeau disait « Utilisateurs », constat de
    // recette) ; /admin/users et /admin/permissions sont des redirections.
    '/equipe': { titleKey: 'pages.team', icon: UserCog },
    '/messaging/templates': { titleKey: 'pages.templates', icon: FileText },
    '/messaging/providers': { titleKey: 'pages.providers', icon: Settings },
    '/campaigns': { titleKey: 'pages.campaigns', icon: Target },
    // #451 — la liste de l'agent (campagnes où il est affecté).
    '/mes-campagnes': { titleKey: 'pages.myCampaigns', icon: Target },
    // L-NAV-4 — UNE entrée : Intégrations et AlloDons sont des sections de la
    // console (`?section=`) ; /settings/integrations et /settings/allodons
    // sont des redirections (le bandeau de la destination s'affiche).
    // L-NAV-5 — les 4 journaux sont les onglets de « Historique » ; /calls,
    // /recordings, /sms/logs et /emails/logs ne sont plus que des redirections.
    '/historique': { titleKey: 'pages.history', icon: History },
    '/settings': { titleKey: 'pages.settings', icon: Settings },
    '/admin/tenants': { titleKey: 'pages.clients', icon: Building2 },
    '/admin/twilio-numbers': { titleKey: 'pages.twilioNumbers', icon: PhoneForwarded },
    '/upgrade': { titleKey: 'pages.upgrade', icon: BarChart3 },
    '/upgrade/success': { titleKey: 'pages.upgrade', icon: BarChart3 },
    '/upgrade/cancel': { titleKey: 'pages.upgrade', icon: BarChart3 },
  }

  // Handle dynamic routes
  // SMS-CAMPAGNE-2 — liste, nouvelle campagne et suivi des campagnes SMS.
  if (pathname === '/campaigns/sms' || pathname.startsWith('/campaigns/sms/')) {
    return { titleKey: 'pages.smsCampaigns', icon: Target }
  }
  if (pathname.startsWith('/campaigns/')) {
    return { titleKey: 'pages.campaignDetails', icon: Target }
  }
  if (pathname.startsWith('/contacts/') && pathname.includes('/timeline')) {
    return { titleKey: 'pages.contactTimeline', icon: Users }
  }
  if (pathname.startsWith('/admin/tenants/')) {
    return { titleKey: 'pages.clientDetails', icon: Building2 }
  }
  // L-NAV-7 — la fiche d'une personne reste sous « Équipe » (le nom de la
  // personne est le titre de la page elle-même).
  if (pathname.startsWith('/equipe/')) {
    return { titleKey: 'pages.team', icon: UserCog }
  }
  // L-NAV-3 : `/admin/campaigns/:id/settings` et `/assignments` ne sont plus
  // des pages — elles redirigent vers les onglets de `/campaigns/:id`, dont le
  // titre (« Détails de la campagne ») s'affiche.

  return routes[pathname] || { titleKey: null, icon: null }
}


export default function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation(['shell', 'common'])
  const { user, logout } = useAuth()
  const { campaigns, activeCampaign, setActiveCampaign, isLoading: campaignsLoading } = useCampaign()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  // Nom affiché de la carte utilisateur (et son infobulle sous lg).
  const userDisplayName = user?.name || user?.email?.split('@')[0] || t('userMenu.defaultName')
  const [currentTime, setCurrentTime] = useState(new Date())
  const userMenuRef = useRef<HTMLDivElement>(null)
  const prefetchedRef = useRef(false)

  // 🎙️ LOT-SUNSET-2 — le bandeau « Micro requis » du châssis (LOT-H9, DA-011)
  // est DÉMONTÉ avec la téléphonie v1 : son unique source d'armement était
  // l'échec de `initializeGlobalTwilioListener()` (v1, supprimé). Le poste v2
  // appartient au `TelephonyProvider` : l'état du Device — panne comprise —
  // est dit par `TelephonyStatusBadge` dans le header, et la scène v2 porte
  // ses propres messages d'erreur. Un bandeau que plus rien ne peut armer
  // serait du code mort (et `noUnusedLocals` casserait le build).

  // 💳 LOT-GATING — état d'essai du tenant (calculé serveur). N'alimente le
  // bandeau que pour un tenant EN ESSAI : l'élément n'est pas rendu sinon,
  // pour que la zone de notification vide n'existe pas (garde H9).
  const { data: gatingStatus } = useGatingStatus(Boolean(user))

  // 💳 LOT #142 / E-20 — solde du compte Twilio (relevé serveur, filtré par
  // rôle). Le hook décide de la VISIBILITÉ (état, rôle, fermeture de session)
  // pour que l'AppShell ne rende l'élément que s'il a quelque chose à dire
  // (garde H9, même raison que TrialBanner). Voisin de l'OfflineDetector et
  // du gating : un bandeau du châssis, pas de la téléphonie (zone gelée).
  const { isSystemAdmin, isAgent } = usePermissions()
  const twilioBalanceBanner = useTwilioBalanceBanner(Boolean(user), isSystemAdmin)

  // 📞 Issue #220 (recette D1) — la barre d'appel globale (`ActiveCallBar`,
  // rendue par le `TelephonyProvider`) est en `position: fixed` en bas : hors
  // du flux, elle recouvrait ENTIÈREMENT le dernier élément d'une page longue
  // (mesuré : 61 px de barre dès 707 px à 1024×768, bouton de la dernière
  // fiche à 707-739 px). Le `<main>` ne portant que `pb-safe`, un espaceur de
  // 80 px est rendu à sa FIN quand — et seulement quand — la barre est à
  // l'écran, d'après la règle de la barre elle-même (même hook).
  const activeCallBarShown = useActiveCallBarShown()

  // 🔔 Position de la pile de toasts — REPLI hors châssis (H0, conservé).
  // La pile vit désormais dans le flux, sous l'en-tête (`NotificationRegion`) :
  // à l'intérieur de l'`AppShell`, plus rien n'est en `position: fixed`, donc
  // plus rien n'est à décaler. `--toast-stack-top` reste publié pour la pile
  // FLOTTANTE de `main.tsx`, qui prend le relais sur les écrans sans châssis et
  // pendant les commits de transition : elle doit alors se poser sous l'en-tête,
  // jamais dessus. Le repli `1rem` de H0 vaut toujours pour `/login`.
  const mainRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const main = mainRef.current
    if (!main) return

    const publishTop = () => {
      const top = Math.round(main.getBoundingClientRect().top)
      document.documentElement.style.setProperty('--toast-stack-top', `${top}px`)
    }

    publishTop()
    window.addEventListener('resize', publishTop)
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publishTop) : null
    observer?.observe(main)

    return () => {
      window.removeEventListener('resize', publishTop)
      observer?.disconnect()
      // Hors AppShell (login…), il n'y a ni en-tête ni bandeau : on rend la
      // main au repli de `FloatingToastStack` plutôt que de laisser un décalage figé.
      document.documentElement.style.removeProperty('--toast-stack-top')
    }
    // LOT-SUNSET-2 : la dépendance `[telephonyError]` tombe avec le bandeau
    // micro v1. Les apparitions/disparitions d'un bandeau (TrialBanner…) sont
    // couvertes par le ResizeObserver sur `<main>` : la position se republie
    // à chaque changement de géométrie, sans dépendance d'état.
  }, [])

  // #259 — le `<main>` survit aux changements de route : sans remise à zéro,
  // une fiche ouverte depuis le bas d'une liste s'ouvrait décalée vers le bas.
  // Chaque changement de CHEMIN repart du haut ; un retour vers une liste
  // restaure ensuite sa position (`useListScrollRestoration`, effet passif qui
  // passe APRÈS cet effet de mise en page). Les changements de paramètres
  // (`?q=…`) ne touchent pas au défilement.
  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [location.pathname])

  // Get current page info
  const pageInfo = getPageInfo(location.pathname, { isAgent: Boolean(isAgent) })
  const PageIcon = pageInfo.icon
  const pageTitle = pageInfo.titleKey ? t(pageInfo.titleKey) : null
  // Titre d'onglet « <Page> · iCall CRM » (libellé = clé i18n déjà utilisée par le bandeau) ;
  // page inconnue : « iCall CRM » seul.
  useDocumentTitle(pageTitle)
  
  // Show campaign selector only for agents
  const showCampaignSelector = user?.role === 'AGENT' && campaigns.length > 0

  // Prefetch frequent pages for instant navigation
  useEffect(() => {
    if (!prefetchedRef.current && user) {
      prefetchedRef.current = true

      // ⚡ PERFORMANCE: Skip prefetch on dialer pages (instant load required)
      // LOT-SUNSET-2 : la surface d'appel est /v2/softphone (les chemins v1
      // redirigent vers elle avant tout rendu du contenu).
      const currentPath = window.location.pathname
      if (currentPath === '/v2/softphone') {
        return
      }
      
      // Prefetch campaigns
      queryClient.prefetchQuery({
        queryKey: ['campaigns'],
        queryFn: () => CampaignsService.getAll(),
        staleTime: 5 * 60 * 1000,
      }).then(() => {
        perfMonitor.logCacheHit('campaigns (prefetch)')
      }).catch(() => {
        // May not have permission
      })
      
      // Prefetch contacts
      queryClient.prefetchQuery({
        queryKey: ['contacts'],
        queryFn: () => ContactsService.getAll(),
        staleTime: 5 * 60 * 1000,
      }).then(() => {
        perfMonitor.logCacheHit('contacts (prefetch)')
      }).catch(() => {
        // May not have permission
      })
    }
  }, [user, queryClient])

  // LOT-SUNSET-2 : ici vivaient l'initialisation du Device v1
  // (`initializeGlobalTwilioListener`), l'enregistrement du handler d'entrant
  // v1, la connexion au WebSocket /calls (synchronisation des modales entre
  // agents) et les handlers accepter/rejeter de l'`IncomingCallModal`.
  // Tout cela appartient désormais au `TelephonyProvider` v2 : Device global,
  // socket /telephony, overlay d'entrant sur toute page, verrou de course
  // côté serveur (concurrence entre agents) — le châssis n'a plus aucun rôle
  // téléphonique.

  // Update time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timeInterval)
  }, [])

  // Fermer le menu quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const handleLogout = () => {
    try {
      setUserMenuOpen(false)
      // Clear tokens et state
      logout()
      // Force immediate redirect to login
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    /* 🎨 UN SEUL FOND, ET C'EST CELUI-CI (LOT-H11, DA-013 / DA-013b / DA-014).
       Le châssis peignait un DÉGRADÉ `slate-900 → slate-800 → slate-900`. Six
       pages en peignaient un autre par-dessus — parfois plus sombre
       (`bg-slate-950` sur /admin/permissions), parfois EXACTEMENT le même
       dégradé mais redémarré à son arrêt le plus sombre contre le milieu plus
       clair de celui du châssis (/monitoring, /admin/campaigns/:id/settings).
       D'où une couture verticale nette à 24 px du bord droit, sans qu'aucune
       couleur ne diffère.
       Un aplat ferme le sujet à la racine : un dégradé ne peut plus être
       « redémarré » puisqu'il n'y en a plus. `background` (slate-950) est le
       fond d'application du §1.3 — les panneaux qui s'y posent sont en
       `background-primary` (slate-900), la barre latérale comprise. */
    <div className="flex h-screen bg-background pl-safe pr-safe">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Ultra-Modern Floating TopBar - iPad optimized */}
        {/* 🧱 LA RANGÉE DU HEADER NE DÉBORDE PLUS, QUEL QUE SOIT SON CONTENU
            (LOT-H11, audit §8.2).
            Le défaut était structurel et invisible depuis un compte admin : en
            rôle AGENT la rangée porte SIX éléments au lieu de cinq — le
            sélecteur de campagne s'ajoute — et à 1180 px le fil d'ariane et le
            nom de l'utilisateur se repliaient sur deux lignes ; la carte
            utilisateur grandissait alors et CREVAIT le conteneur arrondi par le
            haut, sa propre bordure visible par-dessus.
            Le correctif est GÉNÉRIQUE, pas un ajustement de largeur : c'est le
            même motif que le `PageHeader` de H2 — chaque zone peut se réduire
            (`min-w-0`), chaque texte se coupe (`truncate`) et rien de ce qui
            doit rester lisible ne se comprime (`shrink-0`). Aucun contenu, si
            long soit-il, ne peut plus pousser la rangée. */}
        <div className="relative h-16 lg:h-20 flex items-center justify-center px-4 lg:px-8 py-2 lg:py-4 pt-safe z-[100]">
          <div className="relative max-w-7xl w-full">
            {/* Le halo bleu→violet→rose de la version d'origine est retiré
                (DA-015) : il faisait partie des 19 dégradés que la recette a
                comptés sur CHACUN des 24 écrans. */}
            <div className="relative h-12 lg:h-14 bg-background-primary/70 backdrop-blur-2xl border border-border-secondary rounded-card flex items-center justify-between gap-2 px-3 lg:px-6 shadow-2xl">
              {/* Left Section - Page Title Compact */}
              {/* 📏 LA PUCE DU TITRE CÈDE, ELLE NE CHEVAUCHE PLUS LA PASTILLE
                  (recette pause, R2-03/R2-04, portrait 768 px). Elle portait
                  `min-w-0 shrink-0` : `shrink-0` interdisait toute réduction,
                  donc le `truncate` du texte n'avait jamais rien à couper, et
                  quand la zone de gauche (`min-w-0`) rétrécissait sous la
                  pression du centre, la puce DÉBORDAIT de sa zone (mesuré :
                  bord droit à 333 px pour une zone finie à 294 px) et passait
                  SOUS la pastille de statut. Désormais `min-w-0 shrink` : la
                  puce se réduit, le titre se coupe (« Historique du c… »), et
                  le nom complet reste lisible au survol (`title`). */}
              <div className="flex min-w-0 items-center gap-3">
                {pageTitle && PageIcon && (
                  <div className={`${HEADER_CHIP} min-w-0 shrink`} title={pageTitle}>
                    <PageIcon size={16} className="shrink-0 text-primary-400" aria-hidden="true" />
                    <span className="truncate text-foreground">
                      {pageTitle}
                    </span>
                  </div>
                )}
              </div>

              {/* Center Section - Actions Pills */}
              <div className="flex min-w-0 items-center gap-2">
                {/* ✅ Badge d'état du poste v2 : disponibilité + appel + Device.
                    LOT-SUNSET-2 : il n'a plus d'alternative — `ActiveCallBadge`
                    (v1) est supprimé. La pastille « micro requis » (DA-011)
                    tombe avec lui : l'état d'erreur du poste v2 est porté par
                    ce badge. */}
                {/* Le châssis LAISSE SA PLACE à la pastille (composant gelé,
                    `telephony/`) : sans ce conteneur `shrink-0`, la pression de
                    la rangée la comprimait et « Poste en erreur » se repliait
                    sur trois lignes (R2-04). Ce sont la puce du titre, le
                    sélecteur de campagne et le nom de l'utilisateur qui cèdent. */}
                <div className="flex shrink-0">
                  <TelephonyStatusBadge />
                </div>

                {/* Separator — sort avec l'horloge sous lg (sinon deux traits se suivent). */}
                <div className="hidden w-px h-5 shrink-0 bg-border-secondary lg:block"></div>

                {/* Clock Pill */}
                {/* Recette du lot nettoyage (résidu 🟠) : à 768 px portrait, rôle
                    agent, la rangée ne tenait pas dans la barre (carte utilisateur
                    à +31 / +39 px du bord, chevron hors fenêtre) — même carte
                    repliée, la combinaison la plus large (agent, barre ouverte,
                    « Historique du contact », « Poste en erreur ») dépassait
                    encore. L'horloge sort donc de la rangée sous `lg` (décision
                    prise : l'heure est dans la barre d'état de l'iPad) et revient
                    à partir de lg. */}
                <div className={`${HEADER_CHIP} hidden shrink-0 lg:flex`}>
                  <Clock size={16} className="shrink-0 text-primary-400" aria-hidden="true" />
                  <span className="tabular-nums">
                    {/* ⚠️ LOCALE COURANTE, PAS 'en-US' (R-7). L'horloge était figée
                        au format américain : un agent français lisait « 03:16 PM »
                        au lieu de « 15:16 ». C'est la seule donnée du bandeau qui
                        se formate, et elle doit suivre la langue comme le reste.
                        LOT-H1 : cette règle est désormais portée par `formatTime`. */}
                    {formatTime(currentTime)}
                  </span>
                </div>
                
                {/* Campaign Selector (Agents only) */}
                {showCampaignSelector && (
                  <>
                    {/* Separator */}
                    <div className="w-px h-5 shrink-0 bg-border-secondary"></div>

                    {/* Le sélecteur de campagne est le SEUL élément de la rangée
                        autorisé à se réduire (`min-w-0` + `shrink`), parce qu'il
                        est le seul dont le contenu est arbitrairement long — et
                        c'est son arrivée, en rôle agent, qui faisait déborder la
                        rangée (audit §8.2). Il porte la bordure NEUTRE commune :
                        son contour bleu en faisait la sixième forme de pilule. */}
                    <div className={`${HEADER_CHIP} min-w-0 shrink`}>
                      <Briefcase size={16} className="text-primary-400 shrink-0" aria-hidden="true" />
                      {/* ⚠️ TRONCATURE CSS, PAS DE CARACTÈRE AJOUTÉ (R-8). Le
                          `<select>` gardait son habillage natif : à 180 px, la
                          flèche du système se posait par-dessus la fin du nom de
                          campagne — d'où le « glyphe parasite » vu en recette.
                          L'apparence native est retirée, le texte est tronqué par
                          `truncate` (ellipse propre), et la flèche est rendue à
                          côté, hors du champ. */}
                      <select
                        value={activeCampaign?.id || ''}
                        onChange={(e) => {
                          const campaign = campaigns.find(c => c.id === e.target.value)
                          if (campaign) setActiveCampaign(campaign)
                        }}
                        disabled={campaignsLoading}
                        title={activeCampaign?.name}
                        className="appearance-none truncate min-w-0 max-w-[180px] text-t5 text-foreground bg-transparent border-none outline-none cursor-pointer"
                      >
                        {campaigns.map(c => (
                          <option key={c.id} value={c.id} className="bg-background-secondary text-foreground">
                            {c.name} ({c.contactsCount})
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={12} className="text-foreground-secondary shrink-0" aria-hidden="true" />
                    </div>
                  </>
                )}
                
                {/* Separator */}
                <div className="w-px h-5 shrink-0 bg-border-secondary"></div>

                {/* ✅ ENTERPRISE: Missed Calls Badge */}
                <MissedCallsBadge />
                
                {/* Callbacks Notification Center */}
                <CallbacksNotificationCenter
                  onCallbackClick={(callback) => {
                    // ✅ P0: Lancement 1-clic depuis notification
                    // LOT-SCÈNE-POLISH-A (R6) : la scène v2 lit désormais
                    // `location.state` — forme HARMONISÉE des trois émetteurs
                    // `{ phoneNumber, contactName }` (`useRecallPrefill`). Le
                    // composeur est prérempli, JAMAIS composé automatiquement
                    // (l'ancien signal `autoCall` a été retiré : la scène
                    // l'ignore par contrat).
                    console.log('🚀 Launching callback:', callback)
                    navigate('/v2/softphone', {
                      state: {
                        phoneNumber: callback.contactPhone,
                        contactName: callback.contactName,
                      }
                    })
                  }}
                />
              </div>

              {/* Right Section - User */}
              {/* DA-054 : la zone n'est plus `shrink-0` — bornée à 192 px, la
                  carte tronquait « System Administ… » alors que la rangée avait
                  de la place à sa gauche. Elle peut désormais GRANDIR jusqu'à
                  320 px quand la place existe. Le nom complet reste dans le menu.
                  Recette du lot nettoyage (résidu 🟠) : la zone portait aussi
                  `min-w-0` — écrasée sous son propre contenu, elle laissait la
                  carte SORTIR de la barre (+31 / +39 px à 768 px en rôle agent,
                  4 à 16 px à 1024) pendant que la puce du titre gardait du
                  texte. La zone fait désormais PLANCHER : son minimum est la
                  carte (avatar + chevron sous lg, entière à lg), et c'est la
                  puce du titre qui cède. Mesuré : 0 dépassement sur 48
                  combinaisons (harnais du lot). */}
              <div className="flex items-center gap-2">
                {/* Separator */}
                <div className="w-px h-5 shrink-0 bg-border-secondary"></div>

            {/* Premium User Menu */}
            <div className="relative min-w-0" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-expanded={userMenuOpen}
                // Sous lg le bloc nom + rôle est replié : le nom complet reste
                // ici (infobulle), le rôle est dans le menu.
                title={userDisplayName}
                aria-label={userDisplayName}
                className="group flex h-10 min-w-0 max-w-[20rem] items-center gap-2 rounded-pill border border-border-secondary bg-background-secondary px-2 transition-colors hover:border-primary-500/40"
              >
                {/* Avatar : aplat `primary`. Il était en dégradé bleu→violet→rose,
                    et la recette le cite nommément dans DA-015. */}
                <div className="relative shrink-0">
                  <div className="flex h-8 w-8 items-center justify-center rounded-pill bg-primary-600 text-t5 text-white">
                    {avatarInitial(user?.name, user?.email)}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-pill border-2 border-background-primary bg-success-500"></div>
                </div>
                {/* 📏 LE BLOC QUI DÉBORDAIT. Deux lignes de texte dans une rangée
                    de 48 px : dès que le nom se repliait, la carte dépassait le
                    bord haut du conteneur. `min-w-0` + `truncate` sur chacune des
                    deux lignes rendent le repli IMPOSSIBLE, quel que soit le nom. */}
                {/* Recette du lot nettoyage (résidu 🟠) : sous lg (iPad portrait)
                    la carte se replie à l'avatar + chevron — nom et rôle ne sont
                    rendus qu'à partir de lg. C'est, avec l'horloge, ce qui fait
                    tenir la rangée à 768 px en rôle agent. */}
                <div className="hidden min-w-0 text-left lg:block">
                  <div className="truncate text-t5 text-foreground">
                    {userDisplayName}
                  </div>
                  {/* Le rôle est un FAIT, donc neutre (§1.2) — il était bleu accent. */}
                  <div className="truncate text-t6 uppercase text-foreground-secondary">
                    {user?.role || 'USER'}
                  </div>
                </div>
                <ChevronDown
                  size={15}
                  aria-hidden="true"
                  className={`shrink-0 text-foreground-secondary transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Ultra-Premium Dropdown with Glassmorphism */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-3 w-80 bg-background-primary backdrop-blur-2xl border border-border-secondary rounded-card shadow-2xl py-2 z-[9999] animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* Carte d'identité — surface neutre. Le fond dégradé
                      bleu→violet, le halo animé et les deux avatars en dégradé
                      tombent avec DA-015. */}
                  <div className="px-5 py-4 border-b border-border">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-card bg-primary-600 text-t2 text-white">
                        {avatarInitial(user?.name, user?.email)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-t3 text-foreground">
                          {user?.name || t('userMenu.defaultName')}
                        </div>
                        <div className="truncate text-t5 text-foreground-secondary">
                          {user?.email || t('userMenu.noEmail')}
                        </div>
                      </div>
                    </div>
                    {/* Badge de rôle NEUTRE : un rôle n'est ni un succès, ni un
                        avertissement, ni une erreur (§1.2). Il était en vert
                        `emerald`, c'est-à-dire dans le token `success`. */}
                    <span className="inline-flex items-center rounded-pill border border-border-secondary bg-background-secondary px-3 py-1 text-t6 uppercase text-foreground-secondary">
                      {user?.role || 'USER'}
                    </span>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/settings?section=profile')
                        setUserMenuOpen(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 min-h-touch text-t4 text-foreground-secondary hover:bg-background-secondary hover:text-foreground transition-colors group"
                    >
                      <User size={16} className="shrink-0 text-foreground-tertiary group-hover:text-primary-400 transition-colors" aria-hidden="true" />
                      <span>{t('userMenu.profile')}</span>
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="my-1 h-px bg-border"></div>

                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 min-h-touch text-t4 text-danger-400 hover:bg-danger-500/10 hover:text-danger-300 transition-colors group"
                  >
                    <LogOut size={16} className="shrink-0" aria-hidden="true" />
                    <span>{t('userMenu.logout')}</span>
                  </button>
                </div>
              )}
            </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🔔 ZONE DE NOTIFICATION (LOT-H9, DA-002).
            Tout ce qui a quelque chose à dire à l'utilisateur atterrit ICI :
            bandeaux du châssis, états persistants déclarés par l'écran, messages
            éphémères. Dans le flux, donc rien n'est jamais recouvert. Vide, la
            zone ne rend AUCUN élément — l'anatomie verticale de RT-DA1 (§3bis)
            est intacte. */}
        <NotificationRegion>
          {/* 💳 LOT-GATING — bandeau d'essai gratuit, PERSISTANT tant que le
              tenant est en essai : l'agent doit connaître ses quotas (et leur
              rupture) AVANT de tenter un appel — sur refus de token, la scène
              téléphonie gelée n'affiche qu'une erreur générique. La condition
              est ici (et pas seulement dans TrialBanner) : un élément rendu à
              null compterait quand même dans Children.toArray et ferait
              exister la zone à vide (garde H9). */}
          {gatingStatus?.trial && <TrialBanner status={gatingStatus} />}
          {/* 💳 LOT #142 / E-20 — solde Twilio : WARN et CRITICAL pour le
              super-admin seul ; SUSPENDED pour tous (montant et lien console
              pour le super-admin, un seul message « contactez l'assistance »
              pour les autres — décision utilisateur). Refermable pour la
              session, réapparaît si l'état empire. La condition est ici, pas
              seulement dans le composant (garde H9). */}
          {twilioBalanceBanner.visible && twilioBalanceBanner.status && (
            <TwilioBalanceBanner
              status={twilioBalanceBanner.status}
              isSuperAdmin={isSystemAdmin}
              onDismiss={twilioBalanceBanner.dismiss}
            />
          )}
          {/* 🎙️ LOT-SUNSET-2 : le bandeau « Micro requis » v1 (DA-011) est
              démonté — voir le commentaire en tête du composant. */}
        </NotificationRegion>

        {/* Page Content with Pull-to-Refresh */}
        {/* ⬆️ L'ESPACE AU-DESSUS DU TITRE EST PORTÉ ICI, ET NULLE PART AILLEURS
            (LOT-RT-DA1, DA-009).
            La recette DA a mesuré, à 1180 × 820, un vide de 44 px sur /contacts,
            56 px sur /metrics et 64 px sur /admin/team, /campaigns et
            /admin/realtime-stats : le titre de page commençait entre 186 et
            206 px du haut, soit jusqu'à 41 % du premier écran iPad consommé
            avant la première donnée.
            La cause n'était pas un excès mais une ADDITION : ce conteneur
            posait 24 px (`p-6`) et chaque page en rajoutait par-dessus, entre
            12 px (`padding: 0.75rem`) et 32 px (`padding: 2rem`). Trois pages,
            trois totaux.
            Le haut vaut désormais 16 px ici, et les pages qui portent un
            `PageHeader` n'ont plus AUCUN padding haut à leur racine — c'est ce
            que garde `AppShell.spacing.spec.ts`. Avec le bandeau micro affiché
            (il porte `mb-2`), le vide monte à 24 px : les deux bornes restent
            dans la cible 16–24 px de la recette.
            L'horizontal et le bas ne bougent pas : `px-6` comme avant, et le bas
            reste porté par le seul `pb-safe` — l'utilitaire d'`index.css` gagne
            sur les utilitaires Tailwind du même groupe, c'était déjà lui qui
            fixait le bas sous `p-6`. Ajouter un `pb-6` ici ne changerait rien
            et laisserait croire le contraire. */}
        {/* Le `<main>` ne peint plus rien non plus (il posait un voile
            `bg-slate-900/50` sur le dégradé du châssis) : le fond de la zone de
            contenu est celui de l'application, et lui seul. */}
        <main ref={mainRef} data-scroll-root className="flex-1 overflow-y-auto px-6 pt-4 pb-safe">
          <PullToRefresh>
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </PullToRefresh>
          {/* #220 (D1) : dégagement sous le contenu, haut de 80 px ≥ 61 px de
              barre + marge — rendu seulement barre visible, donc le `pb-safe`
              reste seul propriétaire du bas le reste du temps. */}
          {activeCallBarShown && (
            <div aria-hidden="true" data-testid="active-call-bar-spacer" className="h-20 shrink-0" />
          )}
        </main>
      </div>

      {/* ⚡ LOT-SUNSET-2 : l'`IncomingCallModal` v1 est supprimée — les
          entrants sont présentés sur toute page par l'`IncomingCallOverlay`
          du `TelephonyProvider` v2 (App.tsx). */}
    </div>
  )
}
